import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import {
  FORMLY_CONFIG,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { parsePhoneNumber } from 'libphonenumber-js';
import { FormlyRequestProductsComponent } from '../../../demand-request/demand-request-detail/formly-request-products/formly-request-products.component';
import { FormlyButtonComponent } from './formly-button/formly-button-field';
import { FormlyCostcenterSelectOptionsPipe } from './formly-costcenter-select/formly-costcenter-select-options.pipe';
import { FormlyCostcenterSelectComponent } from './formly-costcenter-select/formly-costcenter-select.component';
import { AgGridDocumentAttachmentActionRenderer } from './formly-document-attachments/ag-grid-document-attachment-action-renderer/ag-grid-document-attachment-action-renderer.component';
import { DeleteDocumentConfirmDialogComponent } from './formly-document-attachments/delete-document-confirm-dialog/delete-document-confirm-dialog.component';
import { FormlyDocumentAttachmentsComponent } from './formly-document-attachments/formly-document-attachments.component';
import { FormlyItemAttributeTreeComponent } from './formly-item-attribute-tree/formly-item-attribute-tree.component';
import { FormlyLabelComponent } from './formly-label/formly-label.component';
import { FormlyMatButtonToggleComponent } from './formly-mat-button-toggle/formly-mat-button-toggle.component';
import { FormlyMatChipListComponent } from './formly-mat-chip-list/formly-mat-chip-list.component';
import { FormlyMatCurrencyInputComponent } from './formly-mat-currency-input/formly-mat-currency-input.component';
import { FormlyPurchasersSelectComponent } from './formly-purchasers-select/formly-purchasers-select.component';
import { FormlyRekoleSelectOptionsPipe } from './formly-rekole-select/formly-rekole-select-options.pipe';
import { FormlyRekoleSelectComponent } from './formly-rekole-select/formly-rekole-select.component';
import { FormlyRepeatEditableRatingComponent } from './formly-repeat-editable-rating/formly-repeat-editable-rating.component';
import { FormlyRepeatEditableComponent } from './formly-repeat-editable/formly-repeat-editable.component';
import { FormlyRepeatSectionComponent } from './formly-repeat-section/formly-repeat-section.component';
import { FormlyTextFieldComponent } from './formly-text/text-field';
import { FormlyUserChipListComponent } from './formly-user-chip-list/formly-user-chip-list.component';
import { FormlyUserSelectOptionsPipe } from './formly-user-select/formly-user-select-options.pipe';
import { FormlyUserSelectComponent } from './formly-user-select/formly-user-select.component';
import { FormlyWrapperCheckboxGroupComponent } from './formly-wrapper-checkbox-group/formly-wrapper-checkbox-group.component';
import { FormlyWrapperCurrencyAddonsComponent } from './formly-wrapper-currency-addons/formly-wrapper-currency-addons.component';
import { FormlyWrapperMatAddonsComponent } from './formly-wrapper-mat-addons/formly-wrapper-mat-addons.component';
import { FormlyWrapperMatCardComponent } from './formly-wrapper-mat-card/formly-wrapper-mat-card.component';
import { FormlyWrapperTooltipComponent } from './formly-wrapper-tooltip/formly-wrapper-tooltip.component';
import { matAddonsExtension } from './formly.addons.extension';

function fieldMatchValidator(control: AbstractControl) {
  const { newPassword, repeatedNewPassword } = control.value;

  // avoid displaying the message error when values are empty
  if (!repeatedNewPassword || !newPassword) {
    return null;
  }

  if (repeatedNewPassword === newPassword) {
    return null;
  }

  return { fieldMatch: { message: 'Password Not Matching' } };
}

function EmailValidator(
  control: AbstractControl<string | null>
): ValidationErrors | null {
  if (!control.value) return null;

  return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    control.value
  )
    ? null
    : { email: true };
}

function PhoneValidator(
  control: AbstractControl<string | null>
): ValidationErrors | null {
  if (!control.value) return null;

  try {
    const numberInstance = parsePhoneNumber(control.value, 'CH');

    if (!numberInstance.isValid()) return { phone: true };
  } catch {
    return { phone: true };
  }

  return null;
}

function formlyValidationConfig(translate: TranslateService) {
  return {
    validationMessages: [
      {
        name: 'email',
        message(error: any, field: FormlyFieldConfig) {
          return translate.stream(_('FORM.VALIDATION.EMAIL'), {
            value: field.formControl?.value,
          });
        },
      },
      {
        name: 'phone',
        message(error: any, field: FormlyFieldConfig) {
          return translate.stream(_('FORM.VALIDATION.PHONE'), {
            value: field.formControl?.value,
          });
        },
      },
      {
        name: 'max',
        message(error: any, field: FormlyFieldConfig) {
          return translate.stream(_('FORM.VALIDATION.MAX'), field.props);
        },
      },
      {
        name: 'min',
        message(error: any, field: FormlyFieldConfig) {
          return translate.stream(_('FORM.VALIDATION.MIN'), field.props);
        },
      },
      {
        name: 'minLength',
        message(error: any, field: FormlyFieldConfig) {
          return translate.stream(_('FORM.VALIDATION.MIN_LENGTH'), field.props);
        },
      },
      {
        name: 'maxLength',
        message(error: any, field: FormlyFieldConfig) {
          return translate.stream(_('FORM.VALIDATION.MAX_LENGTH'), field.props);
        },
      },
      {
        name: 'required',
        message(error: any, field: FormlyFieldConfig) {
          return translate.stream(_('FORM.VALIDATION.REQUIRED'), field.props);
        },
      },
      {
        name: 'fieldMatch',
        message(error: any, field: FormlyFieldConfig) {
          return translate.stream(
            _('FORM.VALIDATION.FIELD_MATCH'),
            field.props
          );
        },
      },
    ],
  };
}

@NgModule({
  imports: [
    CommonModule,
    FormlyModule.forRoot({
      validators: [
        { name: 'fieldMatch', validation: fieldMatchValidator },
        { name: 'email', validation: EmailValidator },
        { name: 'phone', validation: PhoneValidator },
      ],
    }),
    FormlyModule.forChild({
      types: [
        {
          name: 'request-products-grid',
          component: FormlyRequestProductsComponent,
        },
        { name: 'repeat', component: FormlyRepeatSectionComponent },
        { name: 'repeat-editable', component: FormlyRepeatEditableComponent },
        {
          name: 'repeat-editable-rating',
          component: FormlyRepeatEditableRatingComponent,
        },

        { name: 'text', component: FormlyTextFieldComponent },
        { name: 'button', component: FormlyButtonComponent },
        {
          wrappers: ['form-field', 'currency-addons'],
          name: 'currency',
          component: FormlyMatCurrencyInputComponent, //FormlyMaskInputComponent,
        },
        {
          wrappers: ['form-field', 'mat-addons'],
          name: 'costcenter-select',
          component: FormlyCostcenterSelectComponent,
        },
        {
          wrappers: ['form-field', 'mat-addons'],
          name: 'user-select',
          component: FormlyUserSelectComponent,
        },
        {
          wrappers: ['form-field', 'mat-addons'],
          name: 'rekole-select',
          component: FormlyRekoleSelectComponent,
        },
        {
          wrappers: ['form-field'],
          name: 'mat-chip-list',
          component: FormlyMatChipListComponent,
        },
        {
          wrappers: ['form-field', 'mat-addons'],
          name: 'user-chip-list',
          component: FormlyUserChipListComponent,
        },
        {
          name: 'document-attachments',
          component: FormlyDocumentAttachmentsComponent,
        },
        {
          name: 'item-attribute-tree',
          component: FormlyItemAttributeTreeComponent,
        },
        {
          name: 'label',
          component: FormlyLabelComponent,
        },
        {
          name: 'purchasers-select',
          component: FormlyPurchasersSelectComponent,
        },
        {
          wrappers: ['form-field'],
          name: 'mat-button-toggle',
          component: FormlyMatButtonToggleComponent,
        },
      ],
      wrappers: [
        {
          name: 'checkbox-group',
          component: FormlyWrapperCheckboxGroupComponent,
        },
        {
          name: 'mat-card',
          component: FormlyWrapperMatCardComponent,
        },
        {
          name: 'mat-addons',
          component: FormlyWrapperMatAddonsComponent,
        },
        {
          name: 'currency-addons',
          component: FormlyWrapperCurrencyAddonsComponent,
        },
        {
          name: 'tooltip',
          component: FormlyWrapperTooltipComponent,
        },
      ],
      extensions: [
        {
          name: 'mat-addons',
          extension: { onPopulate: matAddonsExtension },
        },
      ],
    }),
    FormlyWrapperMatCardComponent,
    FormlyWrapperMatAddonsComponent,
    FormlyMatCurrencyInputComponent,
    FormlyCostcenterSelectComponent,
    FormlyCostcenterSelectOptionsPipe,
    FormlyRekoleSelectComponent,
    FormlyRekoleSelectOptionsPipe,
    FormlyWrapperCheckboxGroupComponent,
    FormlyWrapperCurrencyAddonsComponent,
    FormlyDocumentAttachmentsComponent,
    AgGridDocumentAttachmentActionRenderer,
    FormlyLabelComponent,
    FormlyUserSelectComponent,
    FormlyUserSelectOptionsPipe,
    FormlyMatChipListComponent,
    FormlyUserChipListComponent,
    FormlyRepeatSectionComponent,
    DeleteDocumentConfirmDialogComponent,
    FormlyPurchasersSelectComponent,
    FormlyMatButtonToggleComponent,
  ],
  exports: [],
  providers: [
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: formlyValidationConfig,
      deps: [TranslateService],
    },
  ],
})
export class FormlyExtensionModule {}
